import { Button } from "@volca/ui/components/ui/button";
import { PIPE_UNION } from "@volca/ui/components/ui/empty-machine";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import type { QuickbooksConnection } from "@/business/quickbooks";
import { useConnectQuickbooks } from "@/business/use-connect-quickbooks";
import { formatDate } from "@/core/utils/date";
import { QuickbooksConnectedDetails } from "./quickbooks-connected-details";

/**
 * The card body for each connection status. Connect and reconnect run the same
 * OAuth flow; the API restores a returning company's settings itself.
 */
export const QuickbooksConnectionStatus = ({
  businessId,
  connection,
  canManage,
  onDisconnect,
}: {
  businessId: string;
  connection: QuickbooksConnection;
  canManage: boolean;
  onDisconnect: () => void;
}) => {
  const { connect, isPending } = useConnectQuickbooks(businessId);

  if (connection.status === "connected") {
    return (
      <QuickbooksConnectedDetails
        connection={connection}
        canManage={canManage}
        onDisconnect={onDisconnect}
      />
    );
  }

  const isReconnect = connection.status === "needs_reconnect";

  const description = isReconnect
    ? `QuickBooks stopped accepting Volca’s connection${connection.company_name ? ` to ${connection.company_name}` : ""}. Bills will wait until someone reconnects.`
    : connection.status === "disconnected"
      ? `QuickBooks was disconnected${connection.disconnected_at ? ` on ${formatDate(connection.disconnected_at)}` : ""}. Reconnect the same company to pick up your bill settings and vendor links.`
      : "Connect QuickBooks so bills from your AP invoices are created in QuickBooks.";

  return (
    <MachineEmptyState
      art={PIPE_UNION}
      title={
        isReconnect ? "Reconnect QuickBooks" : "QuickBooks is not connected"
      }
      description={description}
    >
      {canManage && (
        <Button
          type="button"
          size="sm"
          className="w-fit"
          disabled={isPending}
          loading={isPending}
          onClick={() => void connect()}
        >
          {isReconnect ? "Reconnect QuickBooks" : "Connect QuickBooks"}
        </Button>
      )}
    </MachineEmptyState>
  );
};
